import { JournalArticleProps } from '@srcDS/js/components/templates/journal/journalArticle/JournalArticle';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FreeMode, Navigation, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import ArrowLeftNarrow from '../../../atoms/icons/ArrowLeftNarrow';
import ArrowRightNarrow from '../../../atoms/icons/ArrowRightNarrow';
import { Heading } from '../../../typography/heading/Heading';
import ArticleTile from '../articleTile';
import { slideOptions, theme } from './_vars';
import './JournalArticlesSlider.global.scss';
import styles from './JournalArticlesSlider.module.scss';

interface JournalArticlesSliderProps {
  list?: JournalArticleProps['relatedArticles'];
  title?: string;
  currentCategory?: JournalArticleProps['currentCategory'];
  customClasses?: Record<string, string>;
  type?: string;
  showArrows?: boolean;
  showScrollbar?: boolean;
  triggerRoute?: JournalArticleProps['triggerRoute'];
}

const JournalArticlesSlider = ({
  list = [],
  title,
  currentCategory,
  customClasses,
  type = '',
  showArrows = false,
  showScrollbar = true,
  triggerRoute,
}: JournalArticlesSliderProps) => {
  const swiperRef = useRef(null);
  const [listLength, setListLength] = useState(list.length);

  useEffect(() => {
    setListLength(list?.length);
  }, [list]);

  const handleResize = useCallback(() => {
    if (swiperRef.current) {
      setTimeout(() => {
        swiperRef.current.update();
        swiperRef.current.slideTo(0, 0);
      }, 500);
    }
  }, [listLength]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  return (
    <div id="journalRelatedArticles" className={clsx(styles.container, customClasses?.container)}>
      {title && (
        <Heading
          className={styles.title}
          align="center"
          as="h2"
          placement="common"
          textTransform="initial"
          weight={theme === 'saba' ? 'light' : 'semibold'}
        >
          {title}
        </Heading>
      )}
      {list && Array.isArray(list) && list?.length > 0 && (
        <Swiper
          ref={swiperRef}
          navigation={{
            prevEl: '.article-button-prev',
            nextEl: '.article-button-next',
            disabledClass: styles.buttonDisabled,
          }}
          scrollbar={{
            el: '.articles-swiper-scrollbar',
            draggable: true,
            hide: !showScrollbar,
          }}
          modules={[Scrollbar, Navigation, FreeMode]}
          {...slideOptions(swiperRef, listLength)}
        >
          {type === 'homePage' && showArrows && (
            <>
              <div data-carousel-arrow="arrow_left" className={`${styles.buttonPrev} article-button-prev`}>
                <ArrowLeftNarrow />
              </div>
              <div data-carousel-arrow="arrow_right" className={`${styles.buttonNext} article-button-next`}>
                <ArrowRightNarrow />
              </div>
            </>
          )}
          {list.map((article, index) => (
            <SwiperSlide key={index}>
              <ArticleTile {...article} className={styles.articleTile} />
            </SwiperSlide>
          ))}
          {showScrollbar && (
            <div
              tabIndex={0}
              className={`swiper-scrollbar js-swiper-scrollbar articles-swiper-scrollbar ${styles.scrollbar}`}
            />
          )}
        </Swiper>
      )}
    </div>
  );
};

export default JournalArticlesSlider;
